import "./styles.css";
// ⭐ THE APPROVED PLATE SILHOUETTE, generated from the frozen approval. Imported AFTER styles.css so
// it owns the shape outright - see tools/gen_web_plate_shape.py.
import "./plate-shape.generated.css";
// ⭐ THE TYPE LADDER, generated from RevivalUIStyle.SizeFor. Imported BEFORE styles.css so a page can
// still override a rung deliberately, and so the ladder is never hand-written here again.
import "./type-ladder.generated.css";
import { installCssVars } from "./theme.generated";
import { Api, ApiError, setToken } from "./net/api";
import { Router } from "./app/screen";
import { installErrorCapture, setScreen, registerContext, clearContext } from "./app/diagnostics";
import { installBugReporter } from "./ui/report";
import { installTips } from "./ui/tip";
import { mountStage } from "./app/stage";
import { MenuScreen } from "./screens/menu";
import { CampaignScreen } from "./screens/campaign";
import { MatchScreen } from "./screens/match";
import { PlayScreen } from "./screens/play";
import { DecksScreen } from "./screens/decks";
import { ProfileScreen } from "./screens/profile";
import { HelpScreen } from "./screens/help";
import { LoreScreen } from "./screens/lore";
import { DuelsScreen } from "./screens/duels";
import { RanksScreen } from "./screens/ranks";
import { QuadrantsScreen } from "./screens/quadrants";
import { HeroCampaignScreen } from "./screens/heroCampaign";
import { CampaignHubScreen } from "./screens/campaignHub";
import { PracticeScreen } from "./screens/practice";
import { StubIds, StubScreens } from "./screens/stubs";
import { mountBetaMark } from "./ui/betamark";

/**
 * BOOT. Identity first, then the lobby. Nothing renders a game surface before it knows who is here.
 *
 * ⭐ **THE BOOT ORDER IS THE ONE THING THAT MUST NOT BE GUESSED**, and the old client's is written
 * down in our own server: the beta gate is an HMAC cookie, `LoginGuest` DERIVES the identity from
 * that cookie rather than from anything the client sends, and every later route wants the bearer
 * token it returns.
 *
 * 🚨 **A FAILED BOOT MUST SAY WHICH STAGE FAILED.** The old client showed "Something went wrong" for
 * every cause, and it cost days: a renamed enum member, a missing allowlist file and a dead hub all
 * produced the same sentence. Each stage below names itself, and a refusal shows the SERVER'S OWN
 * WORDS - its closed-beta message names the one door that is actually open, which a generic message
 * throws away.
 */

const app = document.getElementById("app");
if (!app) throw new Error("no #app mount point");

installCssVars();

// ⭐ **BEFORE THE FIRST PAINT, AND OUTSIDE EVERY EARLY RETURN.** `main()` returns early on four
// distinct failures, and a mark mounted at the end of it would be absent from exactly the screens a
// confused visitor is most likely to be looking at. Beta is a property of the BUILD, not of a
// successful boot. See ui/betamark.ts.
mountBetaMark();

// ⭐ EVERY SCREEN LIVES ON THE STAGE, so the whole game scales as one object - see app/stage.ts.
const stage = mountStage(app);

function boot(title: string, detail: string, isError = false): void {
  stage.replaceChildren();
  const el = document.createElement("div");
  el.className = isError ? "boot boot--error" : "boot";

  const mark = document.createElement("p");
  mark.className = "boot__mark";
  mark.textContent = "Revival";

  const h = document.createElement("p");
  h.className = "boot__title";
  h.textContent = title;

  const rule = document.createElement("div");
  rule.className = "boot__rule";
  rule.setAttribute("role", "presentation");

  const d = document.createElement("p");
  d.className = "boot__detail";
  // ⚠️ textContent, never innerHTML. This string can carry a server message, and a server message
  // can carry another player's text.
  d.textContent = detail;

  el.append(mark, h, rule, d);

  // ⭐ **PUBLISH THE BOOT STATE TO THE BUG REPORTER.** Caught while testing the reporter against a
  // real sign-in failure: the panel said *"where you are (unknown screen)"*, because the router had
  // never started and so had never published a screen. That is the exact report we most need to be
  // precise - a player whose game would not start - and it was the one carrying the least.
  //
  // ⚠️ The DETAIL is the server's own refusal text, which is the single most useful line in a
  // failed-boot report and is otherwise only on the player's screen.
  setScreen(isError ? "boot:error" : "boot");
  registerContext("boot", () => ({ title, detail, isError }));

  // 🔴 **A FAILURE SCREEN WITH NO CONTROL IS A DEAD END, AND THIS ONE HAD NONE.** A player whose
  // sign-in failed saw a sentence and had nothing to press; the only recovery was knowing to reload.
  // Every other surface in this client is held to "no screen without a way out" - the screen most
  // likely to be somebody's last was the one exempt from it.
  //
  // ⚠️ RELOAD, not a retry of the sign-in call. A partially-booted app retrying into itself is how
  // you get a second session on top of a broken first one; a reload is the state we can reason about.
  if (isError) {
    const act = document.createElement("div");
    act.className = "boot__act";
    const b = document.createElement("button");
    b.type = "button";
    b.className = "plate";
    b.dataset["label"] = "TRY AGAIN";
    const face = document.createElement("span");
    face.className = "plate__face";
    face.setAttribute("aria-hidden", "true");
    const lab = document.createElement("span");
    lab.className = "plate__label";
    lab.textContent = "TRY AGAIN";
    b.append(face, lab);
    b.addEventListener("click", () => location.reload());
    act.append(b);
    el.append(act);
  }

  stage.append(el);
}

/** A stable-per-browser device id. */
function deviceId(): string {
  // ⛔ **"n/a" IS A REAL VALUE THE OLD CLIENT SENT, AND IT COLLAPSED EVERY TESTER ONTO ONE SAVE.**
  // `SystemInfo.deviceUniqueIdentifier` is unavailable in a browser, so every WebGL player logged in
  // as `beta-n/a` - one shared save, one shared ladder, testers overwriting each other with nothing
  // on screen to suggest it. A browser can mint a real one, so it does.
  const KEY = "revival.deviceId";
  let id = localStorage.getItem(KEY);
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem(KEY, id);
  }
  return id;
}

async function main(): Promise<void> {
  // ⭐ FIRST LINE, BEFORE ANYTHING CAN THROW. The error buffer is the half of a bug report a player
  // cannot supply, and an error during boot is the one most worth having. It only listens; it never
  // preventDefault()s, so the console and every other handler still see the same errors.
  installErrorCapture();

  // ⭐ **THE BUG REPORTER GOES UP BEFORE ANYTHING THAT CAN FAIL, AND THAT IS THE WHOLE POINT.**
  // Rebuilt 2026-08-17 after the Unity-to-web migration dropped it (button, bundle code and server
  // route all gone) while the beta notice still asked testers to report bugs.
  //
  // 🔴 THE FIRST VERSION OF THIS CALL SAT AFTER `router.start()`, and a local run caught it within
  // the minute: sign-in failed, `boot()` painted COULD NOT SIGN IN, and there was **no report button
  // on the one screen a player would most want to report from.** A reporter that only appears once
  // the app is healthy cannot report the app being unhealthy. Mounted on `<body>`, so it survives
  // every screen swap and every boot failure below this line.
  installBugReporter();

  /*
   * ⭐ **THE TOOLTIP LAYER, BEFORE ANY SCREEN EXISTS.** Everything is delegated off `document`, so a
   * card drawn ten minutes from now is covered without re-arming. Installed alongside the bug
   * reporter and for the same reason: both must be up before anything that can fail.
   */
  installTips();

  boot("REVIVAL", "Waking the deep north...");

  // ── 1. REACHABILITY, NOT ACCESS ────────────────────────────────────────────────────────────────
  //
  // 🚨 **THIS BLOCK USED TO DECIDE WHO MAY PLAY, AND IT WAS THE THIRD OWNER OF THAT ONE FACT.**
  // It read `if (!gate.address) boot("REVIVAL IS IN CLOSED BETA")` - a hard refusal keyed on a
  // wallet address. But `/api/gate/me` returns a NULL address whenever the gate is switched off,
  // by design and by its own comment ("Returns null rather than 401 when ungated"). So on the day
  // the beta was opened - 2026-08-15, both server switches set correctly, the wallet whitelist
  // emptied and REVIVAL_OPEN_ACCESS=1 live - **every visitor would have been told the game was in
  // CLOSED beta, by the client, from a null that means the exact opposite.**
  //
  // ⭐ THE SERVER IS THE ONLY OWNER OF WHO MAY PLAY, and it already says so in its own words at
  // `Identity/LoginGuest` (the `ClosedBeta` constant, which is itself a one-owner fix for the same
  // class of bug). The client's job is to REPORT that refusal, not to reach its own verdict from a
  // proxy. Note the shape: the two server switches had a deploy-time check forcing them to agree,
  // and this copy sat outside it - an enforcement that covers two of three owners passes while the
  // third fails.
  //
  // The address is still fetched, because the bug reporter attaches it so a tester's report carries
  // their wallet without typing it. A failure here is now non-fatal for exactly that reason.
  let gateAddress: string | null = null;
  try {
    gateAddress = (await Api.gateMe()).address ?? null;
  } catch (e) {
    // Only a genuinely unreachable server is fatal here, and it is a different sentence from a
    // refusal - conflating "we are shut" with "we are down" is what sent testers to retry a door
    // that was never going to open.
    boot("CANNOT REACH THE SERVER", describe(e), true);
    return;
  }
  (window as unknown as { __revivalWallet?: string | null }).__revivalWallet = gateAddress;

  // ── 2. IDENTITY ────────────────────────────────────────────────────────────────────────────────
  let userName = "";
  try {
    const { user } = await Api.loginGuest(deviceId());
    setToken(user.accessToken);
    userName = user.userName;
    console.info(`[boot] signed in as ${userName} (${user.id})`);
  } catch (e) {
    // ⭐ THE SERVER'S REFUSAL IS THE USEFUL HALF - see ClosedBeta in Program.cs, which names the one
    // door that is actually open. This is where a closed beta is reported, and it is the ONLY place:
    // the message comes from the server that made the decision, so it cannot drift out of step with
    // it the way the client's own copy did.
    boot("COULD NOT SIGN IN", describe(e), true);
    return;
  }

  // ── 3. THE LOBBY ───────────────────────────────────────────────────────────────────────────────
  // ⚠️ These are fetched but NOT blocking: a slow statistics route must never stop the menu
  // rendering. The old client awaited its whole lobby graph and sat black when one route was slow.
  void Api.userData().catch((e) => console.warn("[boot] userData failed (non-fatal)", describe(e)));
  void Api.activeSession().catch((e) =>
    console.warn("[boot] activeSession failed (non-fatal)", describe(e)),
  );

  const router = new Router(stage);
  router.register(MenuScreen);
  router.register(CampaignScreen);
  router.register(MatchScreen);
  // ⭐ PLAY is a real screen now, and it must be registered BEFORE the stubs: it claims the
  // `leagues` id, and the stub of the same name would otherwise still be the one the menu reaches.
  router.register(PlayScreen);
  router.register(DecksScreen);
  router.register(ProfileScreen);
  router.register(HelpScreen);
  router.register(LoreScreen);
  router.register(DuelsScreen);
  router.register(RanksScreen);
  router.register(QuadrantsScreen);
  router.register(HeroCampaignScreen);
  router.register(CampaignHubScreen);
  router.register(PracticeScreen);
  // ⭐ **EVERY DESTINATION THE MENU OFFERS IS REGISTERED, EVEN THE UNBUILT ONES.** A menu control
  // that routes nowhere throws from the router, which is loud; but a control the player can press
  // that lands on nothing is the dead end this rebuild exists to remove. A stub says honestly that
  // the screen is not built yet AND offers a way back - see `screens/stubs.ts`.
  for (const s of StubScreens) router.register(s);

  // ⭐ Honours the address bar, so `#/campaign` opens the campaign directly. The walker uses this,
  // and so will the duel invite link.
  router.start("menu");

  // ⛔ THE BOOT CONTEXT HAS DONE ITS JOB. Leaving it registered means every later report carries
  // "COULD NOT SIGN IN" from a boot that actually succeeded, which READS as evidence and points the
  // reader at the wrong failure. Same rule the match screen follows on unmount: a provider must not
  // outlive the state it describes.
  clearContext("boot");


  // ⚠️ Expose the registry for the harness ONLY - it is read, never driven. A tool that navigates by
  // calling into the app would be testing a path no player takes.
  const w = window as unknown as { __revivalScreens?: string[]; __revivalStubs?: readonly string[] };
  w.__revivalScreens = router.ids();
  // ⚠️ The stub list is exported too, so the walker can tell "not built yet" from "built and empty".
  // A walker that scored a stub green would be flattering the build.
  w.__revivalStubs = StubIds;

  // ⭐ A POSITIVE CONTROL FOR THE FONT. Unity baked type into an atlas and the entire redesign once
  // rendered in the FALLBACK face with nothing failing. Here it is one assertion.
  await document.fonts.ready;
  for (const face of ["Revival Display", "Revival Text"]) {
    if (!document.fonts.check(`16px "${face}"`)) {
      console.error(
        `[boot] FONT NOT LOADED: "${face}" is not available, so this screen is rendering in a ` +
          `FALLBACK face. Nothing else will fail and it will look almost right.`,
      );
    }
  }
}

function describe(e: unknown): string {
  if (e instanceof ApiError) {
    // The body is the server's own sentence. Show it.
    return e.body?.trim() ? e.body.slice(0, 400) : `${e.status} from ${e.path}`;
  }
  return e instanceof Error ? e.message : String(e);
}

void main();
